// Google truncates snippets around 155 characters, so anything past this never
// renders in search results.
export const META_DESCRIPTION_MAX_LENGTH = 155;

const ELLIPSIS = '…';

const collapseWhitespace = (text: string): string =>
  text.replace(/\s+/g, ' ').trim();

/**
 * Trim text to a maximum length without cutting a word in half.
 */
export const truncateAtWordBoundary = (
  text: string,
  maxLength: number = META_DESCRIPTION_MAX_LENGTH
): string => {
  const cleaned = collapseWhitespace(text);
  if (cleaned.length <= maxLength) {
    return cleaned;
  }

  const clipped = cleaned.slice(0, maxLength - ELLIPSIS.length);
  const lastSpace = clipped.lastIndexOf(' ');
  const truncated = lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped;

  // Don't leave dangling punctuation in front of the ellipsis
  return `${truncated.replace(/[\s,;:.!?-]+$/, '')}${ELLIPSIS}`;
};

export interface PetitionMetaDescriptionProps {
  subject?: string;
  message?: string;
  fallback?: string;
}

/**
 * Build the meta description for a petition page.
 *
 * Leads with the subject, since that carries the query terms searchers match
 * on, then fills the remaining budget with the letter body. Deliberately omits
 * the signature count — it changes constantly, which churns the snippet, and it
 * spends the most valuable characters on something unsearchable.
 */
export const buildPetitionMetaDescription = ({
  subject = '',
  message = '',
  fallback = '',
}: PetitionMetaDescriptionProps = {}): string => {
  const cleanedSubject = collapseWhitespace(subject);
  const cleanedMessage = collapseWhitespace(message);

  if (!cleanedSubject) {
    return cleanedMessage ? truncateAtWordBoundary(cleanedMessage) : fallback;
  }

  const lead = /[.!?]$/.test(cleanedSubject)
    ? cleanedSubject
    : `${cleanedSubject}.`;

  if (!cleanedMessage || lead.length >= META_DESCRIPTION_MAX_LENGTH) {
    return truncateAtWordBoundary(lead);
  }

  return truncateAtWordBoundary(`${lead} ${cleanedMessage}`);
};

export interface FormatLetterFieldsProps {
  subject?: string;
  message?: string;
  reps?: string;
}

export interface FormattedLetter {
  title: string;
  description: string;
}

export const formatLetterFields = ({
  subject = '',
  message = '',
  reps = '',
}: FormatLetterFieldsProps = {}): FormattedLetter => {
  let title = 'An open letter.';
  let description = 'An open letter.';

  // If there is a subject, use that as the title
  // If there is no subject, use the reps preamble as the title
  if (subject) {
    title = subject;
  } else {
    if (reps) {
      title = `An open letter to ${reps}.`;
    }
  }

  // If there is no message, return the remaining defaults. (should be very rare)
  if (!message) {
    return {
      title,
      description,
    };
  }

  // Remove new lines and replace with space
  description = message.replace(/\n/g, ' ');

  // Collapse series of spaces to a single space
  description = description.replace(/\s+/g, ' ');

  // Remove leading and trailing spaces
  description = description.trim();

  // Limit to 25 words or 155-160 characters
  const words = description.split(' ');
  if (words.length > 25) {
    description = `${words.slice(0, 25).join(' ')}...`;
  }

  // If description is still longer than 160 characters, limit to 160 characters, append `...`
  if (description.length > 160) {
    description = `${description.substring(0, 160)}...`;
  }

  return {
    title,
    description,
  };
};
